import React, { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import TreeItemBase, { TreeItemProps } from '@mui/lab/TreeItem';
import { Stack, Box } from '@mui/material';
import { ReactComponent as ExpandNodeIcon } from '../../assets/arrow-down-tree.svg';
import { ReactComponent as CollapseNodeIcon } from '../../assets/arrow-right-tree.svg';
import { ReactComponent as FileIcon } from '../../assets/file.svg';
import { ColorToken } from '../../style/types';
import { getColor, fontSize, radius } from '../../style/utils';
import {
  type Totals,
  type TreeTotalsNode,
  type TreeTotalsType,
} from '../../types';
import { TestResultsChips } from '../ChipList';
import OverflowTooltip from '../OverflowTooltip';
import { FolderClosedIcon, FolderOpenedIcon } from './FolderIcons';

const Wrapper = styled(TreeItemBase)<{
  $isSelected: boolean;
  $selectionColor: ColorToken;
  $isNodeButton: boolean;
  $isLeaf: boolean;
  $showFolderIcon: boolean;
  $disabled?: boolean;
}>`
  width: 100%;
  min-width: 160px;

  margin-left: ${({ $isLeaf }) => $isLeaf && '11px'};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'all')};

  .Mui-focused {
    background: none !important;
  }

  .Mui-selected {
    background: ${({ theme, $selectionColor, $isSelected }) =>
      $isSelected
        ? `${getColor(theme, $selectionColor)} !important`
        : theme.palette.surface.base};
  }

  &.Mui-selected {
    background: ${({ theme, $selectionColor, $isSelected }) =>
      $isSelected
        ? getColor(theme, $selectionColor)
        : theme.palette.surface.base} !important;
  }

  & .MuiTreeItem-label span {
    font-size: ${fontSize('base')};
  }

  & .MuiTreeItem-label .MuiChip-label {
    font-size: ${fontSize('xs')};
  }

  & .MuiTreeItem-content {
    border-radius: ${radius('sm')};
    height: 28px;
  }

  .MuiTreeItem-iconContainer {
    width: auto;
  }
`;

const ToggleExpandIconWrapper = styled(Stack)`
  width: 18px;
  height: 18px;
  justify-content: center;
  align-items: center;
  pointer-events: all;
`;

const HighlightedSpan = styled.span<{ $highlighted: boolean }>`
  ${({ $highlighted, theme }) =>
    $highlighted && `background-color: ${theme.palette.surface.highlight};`}
`;

type HighlightedTextProps = {
  text: string;
  query: string;
};

const HighlightedText = ({ text, query }: HighlightedTextProps) => {
  const regex = new RegExp(`(${query})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, i) =>
    regex.test(part) ? (
      <HighlightedSpan key={part + i} $highlighted>
        {part}
      </HighlightedSpan>
    ) : (
      part
    ),
  );
};

const ExpandIcon = ({
  showFolderIcon,
  onClick,
  folderIcon,
}: {
  showFolderIcon: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  folderIcon?: JSX.Element;
}) => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
    <ToggleExpandIconWrapper
      onClick={(e) => {
        onClick(e);
        e.stopPropagation();
      }}
    >
      <CollapseNodeIcon />
    </ToggleExpandIconWrapper>
    {showFolderIcon &&
      (folderIcon ? React.cloneElement(folderIcon) : <FolderClosedIcon />)}
  </Stack>
);

const CollapseIcon = ({
  showFolderIcon,
  onClick,
  folderIcon,
}: {
  showFolderIcon: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  folderIcon?: JSX.Element;
}) => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
    <ToggleExpandIconWrapper
      onClick={(e) => {
        onClick(e);
        e.stopPropagation();
      }}
    >
      <ExpandNodeIcon />
    </ToggleExpandIconWrapper>
    {showFolderIcon &&
      (folderIcon ? React.cloneElement(folderIcon) : <FolderOpenedIcon />)}
  </Stack>
);

type Props = TreeItemProps & {
  nodeId: string;
  parentId?: string;
  totals?: Totals;
  totalsType?: TreeTotalsType;
  totalsRequestKey?: string;
  searchQuery?: string;
  isSelected: boolean;
  nodeIcon?: JSX.Element;
  showFolderIcon?: boolean;
  folderIcon?: JSX.Element;
  nodeButton?: JSX.Element;
  onNodeButtonClick?: Function;
  selectionColor?: ColorToken;
  boldLabels?: boolean;
  onExpandCollapse?: Function;
  onNodeClick?: Function;
  scrollIntoView?: boolean;
  showTotals: boolean;
  renderTotals?: (node: TreeTotalsNode) => ReactNode;
};

const TreeItem = ({
  nodeId,
  parentId,
  children,
  label,
  totals,
  totalsType,
  totalsRequestKey,
  isSelected,
  searchQuery,
  nodeIcon = <FileIcon />,
  showFolderIcon = true,
  folderIcon,
  nodeButton,
  onNodeButtonClick,
  selectionColor = 'surface.active',
  boldLabels,
  onExpandCollapse,
  onNodeClick,
  disabled,
  showTotals,
  renderTotals,
  ...props
}: Props) => {
  const ref = useRef<any>();

  useEffect(() => {
    // nested a timeout inside another to make sure the item is scrolled into when there is a lag on large lists
    if (isSelected) {
      setTimeout(() => {
        ref.current?.scrollIntoViewIfNeeded?.();
        setTimeout(() => {
          ref.current?.scrollIntoViewIfNeeded?.();
        }, 300);
      }, 300);
    }
  }, [isSelected]);

  const totalsContent =
    showTotals &&
    (renderTotals && totalsType && totalsRequestKey ? (
      renderTotals({
        id: nodeId,
        kind: totalsType,
        requestKey: totalsRequestKey,
      })
    ) : (
      <TestResultsChips totals={totals} hideEmptyState size="medium" />
    ));

  return (
    <Stack ref={ref} direction="row" sx={{ gap: 0.5 }}>
      <Wrapper
        {...props}
        className={isSelected && !children ? 'Mui-selected' : ''}
        nodeId={nodeId}
        $isSelected={isSelected}
        $selectionColor={selectionColor}
        $isNodeButton={!!nodeButton}
        $isLeaf={!children}
        $showFolderIcon={showFolderIcon}
        onClick={() => !disabled && onNodeClick?.({ id: nodeId, parentId })}
        $disabled={disabled}
        label={
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <OverflowTooltip
              bold={boldLabels}
              placement="right"
              anchorRef={ref}
              tooltipContent={label as string}
            >
              {typeof label === 'string' && !!searchQuery ? (
                <HighlightedText text={label} query={searchQuery} />
              ) : (
                label
              )}
            </OverflowTooltip>
            {totalsContent}
          </Stack>
        }
        icon={!children && React.cloneElement(nodeIcon)}
        expandIcon={
          <ExpandIcon
            showFolderIcon={showFolderIcon}
            onClick={() => onExpandCollapse?.(nodeId)}
            folderIcon={folderIcon}
          />
        }
        collapseIcon={
          <CollapseIcon
            showFolderIcon={showFolderIcon}
            onClick={() => onExpandCollapse?.(nodeId)}
            folderIcon={folderIcon}
          />
        }
      >
        <Box sx={{ ml: 1 }}>{children}</Box>
      </Wrapper>
      {isSelected &&
        !children &&
        nodeButton &&
        React.cloneElement(nodeButton, {
          onClick: () => onNodeButtonClick?.(nodeId),
        })}
    </Stack>
  );
};

export default TreeItem;
